import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { goldGradientButtonSm } from '@/lib/brandGradient'

/*
 * Subtle nudge shown to signed-out visitors on every guest-accessible page
 * except the homepage. AppLayout owns the show/hide decision and renders this
 * just under the Header (the two share one sticky wrapper). "Join VAAGON"
 * routes to /signup, which opens the onboarding wizard.
 *
 * Dismissal is per-session (sessionStorage): the banner stays out of the way
 * for the rest of the visit once dismissed, but a returning guest in a fresh
 * session still gets the prompt.
 */

const DISMISS_KEY = 'guest_banner_dismissed'

const CloseIcon = () => (
  <svg
    aria-hidden
    className="h-4 w-4"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
  </svg>
)

export const GuestBanner = () => {
  const navigate = useNavigate()
  const [dismissed, setDismissed] = useState(
    () =>
      typeof sessionStorage !== 'undefined' &&
      sessionStorage.getItem(DISMISS_KEY) === '1',
  )

  if (dismissed) return null

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1')
    } catch {
      // sessionStorage can throw (private mode / disabled storage); the
      // in-memory state below still hides the banner for this session.
    }
    setDismissed(true)
  }

  return (
    <div className="border-b border-[#F6CB86]/15 bg-[var(--background)]">
      <div className="mx-auto flex w-full max-w-[1600px] items-center gap-3 px-4 py-2 sm:px-6">
        <p className="min-w-0 flex-1 text-xs leading-relaxed text-[var(--foreground-muted)] sm:text-[13px]">
          <span className="font-semibold text-[var(--foreground)]">
            You are viewing as a guest.
          </span>{' '}
          Sign up to validate ideas, earn points and get your own ideas in front
          of thousands.
        </p>
        <button
          type="button"
          onClick={() => navigate('/signup')}
          className={`shrink-0 px-3 py-1.5 text-xs font-semibold ${goldGradientButtonSm}`}
        >
          Join VAAGON
        </button>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss guest banner"
          className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-[var(--foreground-muted)] transition hover:bg-black/5 hover:text-[var(--foreground)] dark:hover:bg-white/5"
        >
          <CloseIcon />
        </button>
      </div>
    </div>
  )
}
